import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";

// Effect-specific prop types
export interface ShockwaveEffectProps {
  duration?: number;
  ringCount?: number;
  colors?: string[];
  offset?: string;
  startScale?: number;
  endScale?: number;
}

export interface EmojiBurstEffectProps {
  duration?: number;
  emojiCount?: number;
  startSize?: number;
  endSize?: number;
}

// Main component props
interface VisualEffectProps {
  children: React.ReactNode;
  shockwaveProps?: ShockwaveEffectProps;
  emojiBurstProps?: EmojiBurstEffectProps;
  className?: string;
}

// Ref API
export interface VisualEffectRef {
  trigger: (params?: { emoji?: string }) => void;
}

interface ActiveEffect {
  id: number;
  type: "shockwave" | "emojiBurst";
  emoji?: string;
}

const ShockwaveRing: React.FC<{
  effect: ActiveEffect;
  index: number;
  duration: number;
  colors: string[];
  offset: string;
  startScale: number;
  endScale: number;
  onComplete: () => void;
}> = ({
  index,
  duration,
  colors,
  offset,
  startScale,
  endScale,
  onComplete,
}) => {
  const color = colors[index % colors.length];
  const delay = index * 100; // Stagger rings

  React.useEffect(() => {
    const timer = setTimeout(onComplete, duration + delay);
    return () => clearTimeout(timer);
  }, [duration, delay, onComplete]);

  return (
    <div
      className={`absolute rounded-full ${color} animate-shockwave pointer-events-none`}
      style={
        {
          top: offset,
          left: offset,
          right: offset,
          bottom: offset,
          animationDuration: `${duration}ms`,
          animationDelay: `${delay}ms`,
          animationFillMode: "both",
          zIndex: 10,
          "--shockwave-start-scale": startScale,
          "--shockwave-end-scale": endScale,
        } as React.CSSProperties
      }
    />
  );
};

const EmojiParticle: React.FC<{
  emoji: string;
  index: number;
  total: number;
  duration: number;
  startSize: number;
  endSize: number;
  onComplete: () => void;
}> = ({ emoji, index, total, duration, startSize, endSize, onComplete }) => {
  const angle = (index / total) * 2 * Math.PI;
  const rotation = Math.random() * 720 - 360; // Random rotation between -360 and 360 degrees

  React.useEffect(() => {
    const timer = setTimeout(onComplete, duration);
    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  return (
    <div
      className="absolute pointer-events-none animate-emoji-burst"
      style={
        {
          left: "50%",
          top: "50%",
          fontSize: `${startSize}px`,
          animationDuration: `${duration}ms`,
          "--emoji-angle": `${angle}rad`,
          "--emoji-rotation": `${rotation}deg`,
          "--emoji-start-size": startSize,
          "--emoji-end-size": endSize,
        } as React.CSSProperties
      }
    >
      {emoji}
    </div>
  );
};

export const VisualEffect = forwardRef<VisualEffectRef, VisualEffectProps>(
  (
    {
      children,
      shockwaveProps: shockwavePropsProp,
      emojiBurstProps: emojiBurstPropsProp,
      className = "",
    },
    ref,
  ) => {
    const [activeEffects, setActiveEffects] = useState<ActiveEffect[]>([]);

    const trigger = (params?: { emoji?: string }) => {
      const effectType = params?.emoji ? "emojiBurst" : "shockwave";
      const newEffect: ActiveEffect = {
        id: Date.now(),
        type: effectType,
        emoji: params?.emoji,
      };
      setActiveEffects((prev) => [...prev, newEffect]);
    };

    useImperativeHandle(ref, () => ({ trigger }));

    const removeEffect = (id: number) => {
      setActiveEffects((prev) => prev.filter((e) => e.id !== id));
    };

    // Default shockwave props
    const shockwaveProps: Required<ShockwaveEffectProps> = {
      duration: shockwavePropsProp?.duration ?? 500,
      ringCount: shockwavePropsProp?.ringCount ?? 2,
      colors: shockwavePropsProp?.colors ?? [
        "border-4 border-white/60",
        "border-4 border-black/30",
      ],
      offset: shockwavePropsProp?.offset ?? "0",
      startScale: shockwavePropsProp?.startScale ?? 1,
      endScale: shockwavePropsProp?.endScale ?? 3,
    };

    // Default emoji burst props
    const emojiBurstProps: Required<EmojiBurstEffectProps> = {
      duration: emojiBurstPropsProp?.duration ?? 1000,
      emojiCount: emojiBurstPropsProp?.emojiCount ?? 16,
      startSize: emojiBurstPropsProp?.startSize ?? 24,
      endSize: emojiBurstPropsProp?.endSize ?? 48,
    };

    return (
      <div className={`relative w-fit ${className}`}>
        {children}
        {activeEffects.map((effect) => {
          if (effect.type === "shockwave") {
            return Array.from({ length: shockwaveProps.ringCount }).map(
              (_, i) => (
                <ShockwaveRing
                  key={`${effect.id}-${i}`}
                  effect={effect}
                  index={i}
                  duration={shockwaveProps.duration}
                  colors={shockwaveProps.colors}
                  offset={shockwaveProps.offset}
                  startScale={shockwaveProps.startScale}
                  endScale={shockwaveProps.endScale}
                  onComplete={() =>
                    i === shockwaveProps.ringCount - 1 &&
                    removeEffect(effect.id)
                  }
                />
              ),
            );
          } else if (effect.type === "emojiBurst" && effect.emoji) {
            return Array.from({ length: emojiBurstProps.emojiCount }).map(
              (_, i) => (
                <EmojiParticle
                  key={`${effect.id}-${i}`}
                  emoji={effect.emoji!}
                  index={i}
                  total={emojiBurstProps.emojiCount}
                  duration={emojiBurstProps.duration}
                  startSize={emojiBurstProps.startSize}
                  endSize={emojiBurstProps.endSize}
                  onComplete={() =>
                    i === emojiBurstProps.emojiCount - 1 &&
                    removeEffect(effect.id)
                  }
                />
              ),
            );
          }
          return null;
        })}
      </div>
    );
  },
);

VisualEffect.displayName = "VisualEffect";
